import type { ELK, ElkNode } from 'elkjs/lib/elk-api';
import { freezeBreadcrumb, reportFreezeEvent } from '@/lib/freeze-reporter';

/**
 * ELK layered layout, off the main thread.
 *
 * elkjs's `elk.bundled.js` with a plain `new ELK()` runs the layout
 * SYNCHRONOUSLY on the calling thread — the promise it returns is cosmetic.
 * On large ownership networks that blocked the UI for seconds and caused the
 * `[client-freeze] unclean-exit` reports on the company-detail page. Layout
 * therefore runs in a Web Worker.
 *
 * The worker is elkjs's official self-contained `elk-worker.min.js`, served
 * verbatim from public/ and driven by `elkjs/lib/elk-api`. It is deliberately
 * NOT bundled: Turbopack does not compile `new Worker(new URL(...))` worker
 * entries — it emits the referenced file as a raw static asset (a previous
 * attempt shipped a `.ts` file served as `video/mp2t`, which browsers refuse
 * to run, silently pushing every layout back onto the main thread).
 *
 * The public/ copy must match the installed elkjs version — the filename
 * carries the version so a mismatch is visible. When upgrading elkjs:
 *   cp node_modules/elkjs/lib/elk-worker.min.js public/elk-worker-<ver>.min.js
 * and update WORKER_URL below.
 *
 * If the worker can't start, crashes, or fails a quick first-use warmup,
 * layout falls back to the main thread — slow but functional — and the
 * fallback is breadcrumbed so it shows up in the freeze forensics.
 */

const WORKER_URL = '/elk-worker-0.11.1.min.js';
/** The warmup lays out a 1-node graph; even slow devices finish well inside this. */
const WARMUP_TIMEOUT_MS = 10_000;

/** Worker failed to load / crashed / never answered — distinct from a layout error. */
class WorkerBrokenError extends Error {}

/**
 * Above this many nodes, refuse to lay out on the main thread at all.
 *
 * The fallback exists so a broken worker still renders a diagram, and for a
 * small graph that costs milliseconds. For a large one it costs *seconds* of
 * a wholly unresponsive tab: measured on prod 2026-08-09, a worker warmup
 * timeout on a big ownership network produced main-thread blocks of 2.3s,
 * 2.5s and 3.3s. A missing diagram is a far smaller harm than a frozen page
 * — the rest of the company profile stays usable, and a reload gets the
 * worker another try.
 */
const MAIN_THREAD_NODE_LIMIT = 60;

/** Thrown when the graph is too big to lay out without freezing the tab. */
export class LayoutUnavailableError extends Error {}

type ElkInstance = {
  elk: ELK;
  /** Rejects with WorkerBrokenError when the worker fires `error`. */
  crashed: Promise<never>;
};

let instance: ElkInstance | null = null;
/**
 * Worker setup can fail transiently (flaky network dropping the worker file
 * or a lazy chunk) — retry on later layouts instead of downgrading the whole
 * tab to main-thread layout forever. Cap it so a genuinely broken
 * environment doesn't re-pay the setup cost on every layout.
 */
const MAX_WORKER_ATTEMPTS = 3;
let workerAttempts = 0;
/** In-flight/settled worker creation + warmup; null = next call may (re)try. */
let workerReady: Promise<boolean> | null = null;

function markBroken(reason: string) {
  freezeBreadcrumb('elk-worker-broken', { reason });
  reportFreezeEvent('elk-worker-broken', {
    op: { name: 'elk-worker-broken', reason, attempt: workerAttempts },
  });
  try {
    instance?.elk.terminateWorker();
  } catch {
    // best-effort cleanup
  }
  instance = null;
  workerReady = null;
}

async function createInstance(): Promise<ElkInstance> {
  const { default: ELKApi } = await import('elkjs/lib/elk-api');
  let rejectCrashed!: (error: Error) => void;
  const crashed = new Promise<never>((_, reject) => {
    rejectCrashed = reject;
  });
  // The rejection is only observed while a layout races against it; swallow
  // it here so an idle crash doesn't surface as an unhandled rejection.
  crashed.catch(() => {});
  const elk = new ELKApi({
    workerUrl: WORKER_URL,
    workerFactory: (url) => {
      const w = new Worker(url!);
      w.addEventListener('error', () =>
        rejectCrashed(new WorkerBrokenError('elk worker failed to load or crashed')),
      );
      return w;
    },
  });
  return { elk, crashed };
}

/**
 * Create the worker on first use and prove it actually answers by laying out
 * a trivial graph. Catches every silent failure mode (asset 404, MIME
 * rejection, protocol mismatch) instead of hanging the real layout forever.
 */
function ensureWorker(): Promise<boolean> {
  if (typeof Worker === 'undefined' || workerAttempts >= MAX_WORKER_ATTEMPTS) {
    return Promise.resolve(false);
  }
  if (!workerReady) {
    workerAttempts += 1;
    workerReady = (async () => {
      let warmupTimer: ReturnType<typeof setTimeout> | undefined;
      try {
        instance = await createInstance();
        await Promise.race([
          instance.elk.layout({
            id: 'warmup',
            layoutOptions: { algorithm: 'org.eclipse.elk.layered' },
            children: [{ id: 'w1', width: 1, height: 1 }],
          }),
          instance.crashed,
          new Promise<never>((_, reject) => {
            warmupTimer = setTimeout(
              () => reject(new WorkerBrokenError('elk worker warmup timed out')),
              WARMUP_TIMEOUT_MS,
            );
          }),
        ]);
        return true;
      } catch (error) {
        markBroken(error instanceof Error ? error.message : String(error));
        return false;
      } finally {
        clearTimeout(warmupTimer);
      }
    })();
  }
  return workerReady;
}

async function layoutOnMainThread(graph: ElkNode): Promise<ElkNode> {
  const { default: ELKBundled } = await import('elkjs/lib/elk.bundled.js');
  return new ELKBundled().layout(graph);
}

let lastMode: 'worker' | 'main-thread' | 'none' = 'none';
/** Which thread the most recent elkLayout call ran on — for freeze forensics. */
export function elkLastMode() {
  return lastMode;
}

export async function elkLayout(graph: ElkNode): Promise<ElkNode> {
  if (await ensureWorker()) {
    const inst = instance;
    if (inst) {
      try {
        lastMode = 'worker';
        freezeBreadcrumb('elk-worker-send');
        return await Promise.race([inst.elk.layout(graph), inst.crashed]);
      } catch (error) {
        // A genuine layout error would fail identically on the main thread —
        // only fall back when the worker itself died mid-flight.
        if (!(error instanceof WorkerBrokenError)) throw error;
        markBroken(error.message);
      }
    }
  }
  const nodeCount = graph.children?.length ?? 0;
  if (nodeCount > MAIN_THREAD_NODE_LIMIT) {
    // Deliberately fail rather than block: the caller renders no diagram,
    // which is recoverable, while a multi-second freeze is not.
    freezeBreadcrumb('elk-layout-refused-main-thread', { nodes: nodeCount });
    reportFreezeEvent('elk-fallback', {
      op: {
        name: 'elk-layout-refused-main-thread',
        nodes: nodeCount,
        limit: MAIN_THREAD_NODE_LIMIT,
        attempt: workerAttempts,
      },
    });
    throw new LayoutUnavailableError(
      `graph too large for main-thread layout (${nodeCount} nodes)`,
    );
  }

  lastMode = 'main-thread';
  freezeBreadcrumb('elk-layout-main-thread-fallback', { nodes: nodeCount });
  reportFreezeEvent('elk-fallback', {
    op: {
      name: 'elk-layout-main-thread-fallback',
      nodes: nodeCount,
      attempt: workerAttempts,
    },
  });
  return layoutOnMainThread(graph);
}
